/* ============================================================
   OVÉE HERO LIGHT: the sun really goes behind the letters. Plain JavaScript and WebGL (no
   libraries), like the 3D bottles (assets/ovee-spin.js).

   The headline stays real text. This draws its letters into a hidden canvas exactly where
   the browser set them, turns that into a distance field (how far each pixel is from a
   letter edge), and a fragment shader lights the edges from wherever the sun disc is on
   screen at that moment: a thin gold rim on the sides facing the sun, strongest near it,
   and while the disc is right behind a letter, the whole outline glows (like an eclipse).
   At night (sun below the banner) nothing is drawn.

   Depth: a mouse (or a tilted Android phone) nudges the sun a few pixels, as if the letters
   stood in front of it. iPhones need a permission prompt for tilt, so they don't get it.
   Reduced motion: the still day scene, lit once, no nudging. No WebGL: nothing changes.
   Turned on by the hero section setting "Sunlit headline" (data-oh-light on the section).
   ============================================================ */
(function () {
  'use strict';

  var INF = 1e20;
  var RANGE = 8;          // the distance field covers 8 CSS px either side of an edge

  var VERT = 'attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.0,1.0);}';
  var FRAG = [
    'precision mediump float;',
    'uniform sampler2D uSdf;',
    'uniform vec2 uRes;',     // canvas size, px
    'uniform vec2 uTexel;',   // one texel of the field
    'uniform vec4 uSun;',     // disc centre (canvas px, y down), radius px, strength
    'uniform vec3 uCol;',     // the disc colour
    'uniform vec3 uK;',       // canvas px per field unit, rim width px, debug
    'void main(){',
    '  vec2 p = vec2(gl_FragCoord.x, uRes.y - gl_FragCoord.y);',
    '  vec2 uv = p/uRes;',
    '  float d = (texture2D(uSdf, uv).r - 0.5)*uK.x;',            // + outside a letter, - inside
    '  if (uK.z > 0.5){ gl_FragColor = d < 0.0 ? vec4(0.5, 0.0, 0.0, 0.5) : vec4(0.0); return; }',
    '  float w = uK.y;',
    '  if (d > 10.0*w || d < -4.0*w){ gl_FragColor = vec4(0.0); return; }',
    '  float gx = texture2D(uSdf, uv + vec2(uTexel.x, 0.0)).r - texture2D(uSdf, uv - vec2(uTexel.x, 0.0)).r;',
    '  float gy = texture2D(uSdf, uv + vec2(0.0, uTexel.y)).r - texture2D(uSdf, uv - vec2(0.0, uTexel.y)).r;',
    '  vec2 n = normalize(vec2(gx, gy) + vec2(1e-6));',             // outward from the letter
    '  vec2 toS = uSun.xy - p;',
    '  float dist = length(toS);',
    '  float R = uSun.z;',
    // the light behind this point: the disc itself, then its warm air fading out
    '  float back = uSun.w*(smoothstep(1.05*R, 0.75*R, dist) + 0.35*exp(-max(dist - R, 0.0)/(0.6*R)));',
    '  float facing = max(dot(n, toS/max(dist, 1e-3)), 0.0);',
    // backlight: a thin bright line where the edge meets the light (it eats a hair into the
    // letter, more on the side facing the sun), glowing just past it. Kept thin and bright:
    // a soft gold blended into the navy letters turns olive.
    '  float side = 0.6 + 0.4*facing;',
    '  float lineIn = exp(-pow(min(d, 0.0)/(0.9*w), 2.0))*back*side;',
    '  float glowOut = exp(-max(d, 0.0)/(2.2*w))*back*side*0.75;',
    '  float t = smoothstep(-0.5, 0.5, d);',
    '  vec3 hot = mix(uCol, vec3(1.0, 0.97, 0.9), 0.55);',
    '  float a = clamp(mix(lineIn, glowOut, t), 0.0, 1.0);',
    '  gl_FragColor = vec4(hot*a, a);',
    '}'
  ].join('\n');

  // Felzenszwalb & Huttenlocher's distance transform, as in Mapbox's tiny-sdf
  function edt1d(grid, offset, stride, length, f, v, z) {
    var q, k, s, r, qr;
    v[0] = 0; z[0] = -INF; z[1] = INF; f[0] = grid[offset];
    for (q = 1, k = 0, s = 0; q < length; q++) {
      f[q] = grid[offset + q * stride];
      var q2 = q * q;
      do { r = v[k]; s = (f[q] - f[r] + q2 - r * r) / (q - r) / 2; } while (s <= z[k] && --k > -1);
      k++; v[k] = q; z[k] = s; z[k + 1] = INF;
    }
    for (q = 0, k = 0; q < length; q++) {
      while (z[k + 1] < q) k++;
      r = v[k]; qr = q - r;
      grid[offset + q * stride] = f[r] + qr * qr;
    }
  }
  function edt(grid, w, h, f, v, z) {
    for (var x = 0; x < w; x++) edt1d(grid, x, w, h, f, v, z);
    for (var y = 0; y < h; y++) edt1d(grid, y * w, 1, w, f, v, z);
  }

  function Light(hero) {
    this.hero = hero;
    this.title = hero.querySelector('.oh-hero__title');
    this.h1 = this.title && this.title.querySelector('.oh-h1');
    this.disc = hero.querySelector('.oh-hero__sun-disc');
    this.reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.debug = /[?&]oh-light-debug/.test(location.search);
    this.par = [0, 0]; this.parTo = [0, 0];
    this.inView = true; this.raf = 0; this.lastDraw = 0; this.key = '';
  }

  Light.prototype.init = function () {
    if (!this.h1 || !this.disc) return;
    var canvas = this.canvas = document.createElement('canvas');
    canvas.className = 'oh-hero__light';
    canvas.setAttribute('aria-hidden', 'true');
    var gl;
    try { gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false }); } catch (e) { gl = null; }
    if (!gl) return;
    this.gl = gl;
    try {
      var prog = gl.createProgram();
      [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]].forEach(function (s) {
        var sh = gl.createShader(s[0]);
        gl.shaderSource(sh, s[1]); gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh));
        gl.attachShader(prog, sh);
      });
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
      gl.useProgram(prog);
    } catch (err) {
      if (window.console) console.warn('Ovée hero light: WebGL shader failed.', err);
      return;
    }
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var U = this.U = {};
    ['uSdf', 'uRes', 'uTexel', 'uSun', 'uCol', 'uK'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
    this.tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(U.uSdf, 0);
    this.title.appendChild(canvas);

    var self = this;
    canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); self.dead = true; canvas.remove(); });
    var t = 0;
    var rebuild = function () { clearTimeout(t); t = setTimeout(function () { self.build(); self.request(true); }, 120); };
    if ('ResizeObserver' in window) new ResizeObserver(rebuild).observe(this.h1);
    else window.addEventListener('resize', rebuild);
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', rebuild);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) {
        self.inView = en[0].isIntersecting;
        if (self.inView) self.request();
      }).observe(this.hero);
    }
    if (!this.reduce) this.bindDepth();
    this.build();
    this.request(true);
  };

  // The letters as the browser set them: each character drawn at its own place on the page
  // (so kerning and letter-spacing match exactly), then a signed distance field from them.
  Light.prototype.build = function () {
    if (this.dead) return;
    var h1 = this.h1, cs = getComputedStyle(h1), fs = parseFloat(cs.fontSize) || 60;
    var tb = this.title.getBoundingClientRect(), hb = h1.getBoundingClientRect();
    if (!hb.width) return;
    var pad = Math.ceil(RANGE + 2);
    // the box sits on whole screen pixels, so the canvas is drawn 1:1 over the text
    var dp = window.devicePixelRatio || 1;
    var ox = Math.floor((hb.left - pad) * dp) / dp, oy = Math.floor((hb.top - pad) * dp) / dp;
    var bw = Math.ceil((hb.right + pad - ox) * dp) / dp, bh = Math.ceil((hb.bottom + pad - oy) * dp) / dp;
    // the field: at least 2x (finer edges on 1x screens), at most ~0.9 megapixels
    var s = Math.max(2, Math.min(dp, 3));
    if (bw * bh * s * s > 900000) s = Math.sqrt(900000 / (bw * bh));
    var W = Math.max(1, Math.round(bw * s)), H = Math.max(1, Math.round(bh * s));
    var c2 = document.createElement('canvas');
    c2.width = W; c2.height = H;
    var x = c2.getContext('2d');
    x.scale(s, s);
    x.fillStyle = '#fff';
    x.textBaseline = 'alphabetic';
    x.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + fs + 'px ' + cs.fontFamily;
    var walker = document.createTreeWalker(h1, NodeFilter.SHOW_TEXT, null), node, range = document.createRange();
    while ((node = walker.nextNode())) {
      if (node.parentNode && node.parentNode.closest && node.parentNode.closest('svg')) continue;
      var txt = node.nodeValue;
      for (var i = 0; i < txt.length; i++) {
        var ch = txt.charAt(i);
        if (/\s/.test(ch)) continue;
        range.setStart(node, i); range.setEnd(node, i + 1);
        var r = range.getClientRects()[0];
        if (!r || !r.width) continue;
        // the character's box starts at the font's ascent above the baseline (measured
        // against the browser's own baseline, 16 Oct)
        var asc = x.measureText(ch).fontBoundingBoxAscent;
        var base = asc !== undefined ? r.top + asc : r.top + r.height * 0.75;
        x.fillText(ch, r.left - ox, base - oy);
      }
    }
    var data = x.getImageData(0, 0, W, H).data, len = W * H;
    var outer = new Float64Array(len), inner = new Float64Array(len);
    var n = Math.max(W, H);
    var f = new Float64Array(n), v = new Uint16Array(n), z = new Float64Array(n + 1);
    for (var j = 0; j < len; j++) {
      var a = data[j * 4 + 3] / 255;
      if (a === 0) { outer[j] = INF; inner[j] = 0; }
      else if (a === 1) { outer[j] = 0; inner[j] = INF; }
      else { var dd = 0.5 - a; outer[j] = dd > 0 ? dd * dd : 0; inner[j] = dd < 0 ? dd * dd : 0; }
    }
    edt(outer, W, H, f, v, z);
    edt(inner, W, H, f, v, z);
    var out = new Uint8Array(len), k = 127 / (RANGE * s);
    for (j = 0; j < len; j++) {
      var dist = Math.sqrt(outer[j]) - Math.sqrt(inner[j]);
      out[j] = Math.max(0, Math.min(255, Math.round(128 + dist * k)));
    }
    var gl = this.gl;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, W, H, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, out);
    this.fieldW = W; this.fieldH = H;
    this.fs = fs;
    // the canvas covers the same box, at the screen's sharpness
    var dpr = Math.min(dp, 2), cv = this.canvas;
    cv.style.left = (ox - tb.left) + 'px';
    cv.style.top = (oy - tb.top) + 'px';
    cv.style.width = bw + 'px';
    cv.style.height = bh + 'px';
    cv.width = Math.round(bw * dpr);
    cv.height = Math.round(bh * dpr);
    this.dpr = dpr;
    this.ready = true;
  };

  Light.prototype.bindDepth = function () {
    var self = this;
    window.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      self.parTo = [e.clientX / window.innerWidth * 2 - 1, e.clientY / window.innerHeight * 2 - 1];
      self.request();
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', function () { self.parTo = [0, 0]; self.request(); });
    // Android tilt (iPhones ask permission first, so they never send these)
    window.addEventListener('deviceorientation', function (e) {
      if (e.gamma == null || e.beta == null) return;
      self.parTo = [Math.max(-1, Math.min(1, e.gamma / 25)), Math.max(-1, Math.min(1, (e.beta - 45) / 25))];
      self.request();
    }, { passive: true });
  };

  Light.prototype.request = function (force) {
    if (force) this.key = '';
    var self = this;
    if (!this.raf && !this.dead) this.raf = requestAnimationFrame(function (now) { self.raf = 0; self.frame(now); });
  };

  Light.prototype.frame = function (now) {
    if (!this.inView || !this.ready) return;
    var moving = !this.reduce;
    // the sun moves slowly: 30 frames a second is plenty
    if (moving && now - this.lastDraw < 30) { this.request(); return; }
    // ease the nudge towards the pointer
    var p = this.par, t = this.parTo;
    p[0] += (t[0] - p[0]) * 0.08; p[1] += (t[1] - p[1]) * 0.08;
    var amp = this.fs * 0.06;
    this.hero.style.setProperty('--oh-par-x', (p[0] * amp).toFixed(2) + 'px');
    this.hero.style.setProperty('--oh-par-y', (p[1] * amp * 0.6).toFixed(2) + 'px');
    var s = this.disc.getBoundingClientRect(), c = this.canvas.getBoundingClientRect(), hb = this.hero.getBoundingClientRect();
    var fill = getComputedStyle(this.disc).fill;
    var key = [Math.round((s.left - c.left) * 4), Math.round((s.top - c.top) * 4), Math.round(s.width * 4), fill, c.width, c.height].join(',');
    if (key !== this.key) { this.key = key; this.draw(s, c, hb, fill); this.lastDraw = now; }
    if (moving) this.request();
  };

  Light.prototype.draw = function (s, c, hb, fill) {
    var gl = this.gl, U = this.U, dpr = this.dpr, cv = this.canvas;
    var cx = s.left + s.width / 2, cy = s.top + s.height / 2, R = s.width / 2;
    // the sun lights the letters once it has cleared the bottom of the banner
    var t = Math.max(0, Math.min(1, (hb.bottom - cy + R * 0.2) / (R * 0.7)));
    var strength = t * t * (3 - 2 * t);
    gl.viewport(0, 0, cv.width, cv.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (strength <= 0.001 && !this.debug) return;
    var m = String(fill).match(/[\d.]+/g) || [255, 186, 59];
    gl.uniform2f(U.uRes, cv.width, cv.height);
    gl.uniform2f(U.uTexel, 1 / this.fieldW, 1 / this.fieldH);
    gl.uniform4f(U.uSun, (cx - c.left) * dpr, (cy - c.top) * dpr, R * dpr, strength);
    gl.uniform3f(U.uCol, m[0] / 255, m[1] / 255, m[2] / 255);
    gl.uniform3f(U.uK, 2 * RANGE * dpr, Math.max(0.5, this.fs * 0.008) * dpr, this.debug ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('.oh-hero[data-oh-light]'), function (hero) {
      if (hero.__ohLight) return;
      var l = hero.__ohLight = new Light(hero);
      // build from the real font, not a fallback
      var go = function () { l.init(); };
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(go, go);
      else go();
    });
  }
  // Build the distance field after the page has loaded and the browser is idle (Oct 2026 audit):
  // building it at DOMContentLoaded cost ~3 s of main-thread time on a throttled phone in
  // Lighthouse, the largest share of the homepage's blocking time. The effect looks the same.
  function startWhenIdle() {
    var run = function () { start(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 400);
  }
  if (document.readyState === 'complete') startWhenIdle();
  else window.addEventListener('load', startWhenIdle, { once: true });
  document.addEventListener('shopify:section:load', start);
})();
